/**
 * Kind-aware muscle-memory practice queue.
 * Same class notes → practice that matches the class kind (rhythm stays rhythm,
 * chords stay chords, etc.). Pitch-walk drills only for drill/scale/speed kinds.
 */

import { getPieceById } from "../music/library.js";
import type { HandId, MusicPiece, NoteEvent } from "../music/types.js";
import type { ClassKind, ClassSegment, TutorClass } from "./types.js";

export type PracticePhase = "warm" | "build" | "vary" | "lock" | "seal";

export interface PracticeSlot {
  piece: MusicPiece;
  label: string;
  bpm?: number;
  metronomeLocked?: boolean;
  phase: PracticePhase;
}

type FingerId = 1 | 2 | 3 | 4 | 5;

interface PitchFinger {
  midi: number;
  finger?: FingerId;
  hand?: HandId;
}

interface Variant {
  title: string;
  notes: NoteEvent[];
  difficulty: number;
}

function pitchSetFromPiece(piece: MusicPiece): PitchFinger[] {
  const byMidi = new Map<number, PitchFinger>();
  for (const n of piece.notes) {
    if (!byMidi.has(n.midi)) {
      byMidi.set(n.midi, {
        midi: n.midi,
        finger: n.finger,
        hand: n.hand ?? piece.hand,
      });
    }
  }
  return [...byMidi.values()].sort((a, b) => a.midi - b.midi);
}

function note(
  beat: number,
  duration: number,
  p: PitchFinger,
  velocity = 92,
): NoteEvent {
  return {
    beat,
    duration,
    midi: p.midi,
    velocity,
    hand: p.hand,
    finger: p.finger,
  };
}

function sequenceNotes(
  pitches: PitchFinger[],
  beatStep: number,
  startBeat = 0,
): NoteEvent[] {
  return pitches.map((p, i) => note(startBeat + i * beatStep, beatStep, p));
}

function cloneNotes(notes: NoteEvent[], beatOffset = 0): NoteEvent[] {
  return notes.map((n) => ({ ...n, beat: n.beat + beatOffset }));
}

function shiftNotes(notes: NoteEvent[], semitones: number): NoteEvent[] {
  return notes.map((n) => ({
    ...n,
    midi: Math.max(21, Math.min(108, n.midi + semitones)),
  }));
}

function makePiece(
  id: string,
  title: string,
  seed: MusicPiece,
  notes: NoteEvent[],
  bpm: number,
): MusicPiece {
  return {
    id,
    title,
    category: seed.category ?? "exercise",
    bpm,
    timeSignature: seed.timeSignature ?? "4/4",
    key: seed.key,
    hand: seed.hand,
    stage: seed.stage,
    description: `Kind-faithful practice from ${seed.title}`,
    notes,
    pedalCues: seed.pedalCues,
  };
}

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function durationPattern(seed: MusicPiece): number[] {
  const sorted = [...seed.notes].sort((a, b) => a.beat - b.beat || a.midi - b.midi);
  const seen = new Set<number>();
  const durs: number[] = [];
  for (const n of sorted) {
    const key = Math.round(n.beat * 1000);
    if (seen.has(key)) continue;
    seen.add(key);
    durs.push(n.duration);
  }
  return durs.length ? durs : [1];
}

/** Notes grouped into simultaneous stacks (chords / polyphony cells). */
function chordStacks(seed: MusicPiece): NoteEvent[][] {
  const byBeat = new Map<number, NoteEvent[]>();
  for (const n of seed.notes) {
    const key = Math.round(n.beat * 1000) / 1000;
    const arr = byBeat.get(key) ?? [];
    arr.push({ ...n });
    byBeat.set(key, arr);
  }
  return [...byBeat.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, notes]) => notes.sort((a, b) => a.midi - b.midi));
}

function applyRhythmToPitches(
  pitches: PitchFinger[],
  pattern: number[],
  loops = 1,
): NoteEvent[] {
  const notes: NoteEvent[] = [];
  let beat = 0;
  let pi = 0;
  for (let L = 0; L < loops; L++) {
    for (const dur of pattern) {
      const p = pitches[pi % pitches.length];
      notes.push(note(beat, dur, p, dur >= 1.5 ? 96 : 88));
      beat += dur;
      pi += 1;
    }
  }
  return notes;
}

function blockChordSequence(
  stacks: NoteEvent[][],
  holdBeats: number,
): NoteEvent[] {
  const out: NoteEvent[] = [];
  let beat = 0;
  for (const stack of stacks) {
    const dur = stack[0]?.duration ?? holdBeats;
    for (const n of stack) {
      out.push({ ...n, beat, duration: holdBeats || dur });
    }
    beat += holdBeats || dur;
  }
  return out;
}

function arpeggiateStacks(stacks: NoteEvent[][], step = 0.5): NoteEvent[] {
  const out: NoteEvent[] = [];
  let beat = 0;
  for (const stack of stacks) {
    for (const n of stack) {
      out.push({ ...n, beat, duration: step });
      beat += step;
    }
    // Resolve back to block
    for (const n of stack) {
      out.push({ ...n, beat, duration: step * 2 });
    }
    beat += step * 2;
  }
  return out;
}

function invertStack(stack: NoteEvent[], amount: number): NoteEvent[] {
  if (stack.length < 2) return stack.map((n) => ({ ...n }));
  const sorted = [...stack].sort((a, b) => a.midi - b.midi);
  const rotated = [
    ...sorted.slice(amount % sorted.length),
    ...sorted.slice(0, amount % sorted.length).map((n) => ({
      ...n,
      midi: n.midi + 12,
    })),
  ];
  return rotated;
}

/** Pitch-walk vocabulary — only for drill / scale / speed / blind technique. */
function pitchWalkVariants(
  seed: MusicPiece,
  classId: string,
  salt: number,
): Variant[] {
  const set = pitchSetFromPiece(seed);
  if (set.length === 0) {
    return [{ title: seed.title, notes: seed.notes, difficulty: 1 }];
  }
  const rnd = mulberry32(hashSeed(`${classId}:${seed.id}:${salt}`));
  const out: Variant[] = [];
  const asc = set;
  const desc = [...set].reverse();

  out.push({ title: "Walk up", notes: sequenceNotes(asc, 1), difficulty: 1 });
  out.push({ title: "Walk down", notes: sequenceNotes(desc, 1), difficulty: 1 });
  out.push({
    title: "Up & down",
    notes: sequenceNotes([...asc, ...desc.slice(1)], 1),
    difficulty: 2,
  });

  if (set.length >= 3) {
    const a = set[0];
    const b = set[Math.floor(set.length / 2)];
    const c = set[set.length - 1];
    out.push({
      title: "Skip shape",
      notes: sequenceNotes([a, b, c, b, a, b, c], 1),
      difficulty: 3,
    });
  }

  {
    const notes: NoteEvent[] = [];
    let beat = 0;
    for (const p of asc) {
      for (let k = 0; k < 4; k++) {
        notes.push(note(beat, 0.25, p, 85 + k * 3));
        beat += 0.25;
      }
    }
    out.push({ title: "Pulse drills", notes, difficulty: 3 });
  }

  for (let round = 0; round < 6; round++) {
    const len = 8 + round * 2;
    const path: PitchFinger[] = [];
    let prev = Math.floor(rnd() * set.length);
    path.push(set[prev]);
    for (let i = 1; i < len; i++) {
      let next = prev;
      if (rnd() < 0.7) {
        next = Math.max(0, Math.min(set.length - 1, prev + (rnd() < 0.5 ? -1 : 1)));
      } else {
        next = Math.floor(rnd() * set.length);
      }
      path.push(set[next]);
      prev = next;
    }
    out.push({
      title: `Combo ${String.fromCharCode(65 + (salt * 6 + round) % 26)}`,
      notes: sequenceNotes(path, round >= 3 ? 0.5 : 1),
      difficulty: 4 + Math.floor(round / 2),
    });
  }

  out.push({ title: seed.title, notes: seed.notes, difficulty: 2 });
  return out;
}

function rhythmVariants(seed: MusicPiece): Variant[] {
  const set = pitchSetFromPiece(seed);
  const pattern = durationPattern(seed);
  const out: Variant[] = [];
  out.push({ title: `${seed.title} · exact`, notes: seed.notes, difficulty: 1 });
  out.push({
    title: "Rhythm cell ×2",
    notes: applyRhythmToPitches(set, pattern, 2),
    difficulty: 2,
  });
  out.push({
    title: "Rhythm reverse pitches",
    notes: applyRhythmToPitches([...set].reverse(), pattern, 2),
    difficulty: 3,
  });
  const rotated = [...pattern.slice(1), pattern[0]];
  out.push({
    title: "Rhythm rotate",
    notes: applyRhythmToPitches(set, rotated, 2),
    difficulty: 3,
  });
  // Keep durations, force metronome-friendly long-short
  const ssl = pattern.flatMap((d) => (d >= 1 ? [0.5, 0.5, d] : [d]));
  out.push({
    title: "Short-short-long on class rhythm",
    notes: applyRhythmToPitches(set, ssl.length ? ssl : [0.5, 0.5, 1], 2),
    difficulty: 4,
  });
  out.push({
    title: `${seed.title} · again`,
    notes: cloneNotes(seed.notes),
    difficulty: 2,
  });
  // Half-speed hold of same rhythm
  out.push({
    title: "Slow values",
    notes: applyRhythmToPitches(
      set,
      pattern.map((d) => d * 2),
      1,
    ),
    difficulty: 2,
  });
  return out;
}

function chordVariants(seed: MusicPiece): Variant[] {
  const stacks = chordStacks(seed);
  const out: Variant[] = [];
  out.push({ title: `${seed.title} · blocks`, notes: seed.notes, difficulty: 1 });
  if (stacks.length === 0) {
    return out;
  }
  out.push({
    title: "Block holds",
    notes: blockChordSequence(stacks, 2),
    difficulty: 2,
  });
  out.push({
    title: "Arpeggio → block",
    notes: arpeggiateStacks(stacks, 0.5),
    difficulty: 3,
  });
  out.push({
    title: "1st inversion circuit",
    notes: blockChordSequence(
      stacks.map((s) => invertStack(s, 1)),
      1.5,
    ),
    difficulty: 4,
  });
  out.push({
    title: "2nd inversion circuit",
    notes: blockChordSequence(
      stacks.map((s) => invertStack(s, 2)),
      1.5,
    ),
    difficulty: 4,
  });
  // Broken then solid again using seed order
  {
    const notes: NoteEvent[] = [];
    let beat = 0;
    for (const stack of stacks) {
      for (const n of stack) {
        notes.push({ ...n, beat, duration: 0.5 });
        beat += 0.5;
      }
      for (const n of stack) {
        notes.push({ ...n, beat, duration: 2 });
      }
      beat += 2;
    }
    out.push({ title: "Broken then solid", notes, difficulty: 3 });
  }
  out.push({
    title: `${seed.title} · lock`,
    notes: cloneNotes(seed.notes),
    difficulty: 2,
  });
  return out;
}

function polyphonyVariants(seed: MusicPiece): Variant[] {
  const out: Variant[] = [];
  out.push({ title: `${seed.title} · both hands`, notes: seed.notes, difficulty: 1 });
  const rh = seed.notes.filter((n) => (n.hand ?? seed.hand) !== "left");
  const lh = seed.notes.filter((n) => (n.hand ?? seed.hand) === "left");
  if (rh.length) out.push({ title: "RH voice alone", notes: rh, difficulty: 2 });
  if (lh.length) out.push({ title: "LH voice alone", notes: lh, difficulty: 2 });
  out.push({ title: "Hands together again", notes: cloneNotes(seed.notes), difficulty: 3 });
  if (rh.length && lh.length) {
    // Stagger: RH then add LH
    const maxBeat = Math.max(...seed.notes.map((n) => n.beat + n.duration), 0);
    out.push({
      title: "Layer in LH",
      notes: [...cloneNotes(rh), ...cloneNotes(lh, maxBeat + 1)],
      difficulty: 4,
    });
  }
  out.push({
    title: `${seed.title} · slow`,
    notes: seed.notes.map((n) => ({ ...n, beat: n.beat * 1.5, duration: n.duration * 1.5 })),
    difficulty: 2,
  });
  return out;
}

function authoredHeavyVariants(seed: MusicPiece, label: string): Variant[] {
  const out: Variant[] = [];
  out.push({ title: `${seed.title} · ${label}`, notes: seed.notes, difficulty: 1 });
  out.push({ title: `${seed.title} · repeat`, notes: cloneNotes(seed.notes), difficulty: 2 });
  out.push({
    title: `${seed.title} · octave up`,
    notes: shiftNotes(seed.notes, 12),
    difficulty: 3,
  });
  out.push({
    title: `${seed.title} · octave down`,
    notes: shiftNotes(seed.notes, -12),
    difficulty: 3,
  });
  // Soft dynamics pass
  out.push({
    title: `${seed.title} · soft touch`,
    notes: seed.notes.map((n) => ({ ...n, velocity: Math.min(70, n.velocity ?? 70) })),
    difficulty: 2,
  });
  out.push({
    title: `${seed.title} · full take`,
    notes: cloneNotes(seed.notes),
    difficulty: 4,
  });
  return out;
}

function earVariants(seed: MusicPiece): Variant[] {
  // Short phrase chunks for echo practice — preserve melodic contour
  const sorted = [...seed.notes].sort((a, b) => a.beat - b.beat);
  const out: Variant[] = [
    { title: `${seed.title} · listen & play`, notes: seed.notes, difficulty: 1 },
  ];
  if (sorted.length >= 4) {
    const mid = Math.ceil(sorted.length / 2);
    const a = sorted.slice(0, mid);
    const b = sorted.slice(mid);
    const aEnd = Math.max(...a.map((n) => n.beat + n.duration));
    out.push({ title: "Echo phrase A", notes: a, difficulty: 2 });
    out.push({ title: "Echo phrase B", notes: b.map((n) => ({ ...n, beat: n.beat - aEnd })), difficulty: 2 });
    out.push({ title: "Full echo", notes: cloneNotes(seed.notes), difficulty: 3 });
  }
  out.push({ title: `${seed.title} · memory seal`, notes: cloneNotes(seed.notes), difficulty: 4 });
  return out;
}

function variantsForKind(
  kind: ClassKind,
  seed: MusicPiece,
  classId: string,
  salt: number,
): Variant[] {
  switch (kind) {
    case "rhythm":
    case "polyrhythm":
      return rhythmVariants(seed);
    case "chord":
      return chordVariants(seed);
    case "polyphony":
    case "independence":
      return polyphonyVariants(seed);
    case "sight":
    case "theory":
      return authoredHeavyVariants(seed, "read");
    case "ear":
      return earVariants(seed);
    case "pedal":
      return authoredHeavyVariants(seed, "pedal");
    case "repertoire":
    case "performance":
    case "improvisation":
      return authoredHeavyVariants(seed, "performance");
    case "blind":
      // Blind = same technique content, UI hides guides via assessment
      return pitchWalkVariants(seed, classId, salt);
    case "drill":
    case "scale":
    case "speed":
    default:
      return pitchWalkVariants(seed, classId, salt);
  }
}

/** Fraction of session reserved for authored warm-up segments (by kind). */
function authoredWarmFraction(kind: ClassKind): number {
  switch (kind) {
    case "repertoire":
    case "performance":
    case "improvisation":
    case "pedal":
      return 0.45;
    case "rhythm":
    case "chord":
    case "sight":
    case "theory":
    case "ear":
    case "polyphony":
    case "independence":
    case "polyrhythm":
      return 0.35;
    case "blind":
      return 0.25;
    default:
      return 0.15;
  }
}

function seedPieces(tutorClass: TutorClass): MusicPiece[] {
  const seen = new Set<string>();
  const out: MusicPiece[] = [];
  for (const seg of tutorClass.segments) {
    if (seen.has(seg.pieceId)) continue;
    const p = getPieceById(seg.pieceId);
    if (p) {
      seen.add(seg.pieceId);
      out.push(p);
    }
  }
  return out;
}

function phaseForProgress(t: number): PracticePhase {
  if (t < 0.18) return "warm";
  if (t < 0.4) return "build";
  if (t < 0.7) return "vary";
  if (t < 0.88) return "lock";
  return "seal";
}

function bpmForPhase(
  baseBpm: number,
  phase: PracticePhase,
  seg?: ClassSegment,
): number {
  const lockedBpm = seg?.bpm ?? baseBpm;
  switch (phase) {
    case "warm":
      return Math.max(48, Math.round(baseBpm * 0.85));
    case "build":
      return Math.round(baseBpm);
    case "vary":
      return Math.round(baseBpm * 1.05);
    case "lock":
      return Math.round(lockedBpm * 1.08);
    case "seal":
      return Math.round(lockedBpm * 1.15);
  }
}

/**
 * Build a progressive queue sized for the selected session length.
 * Variants stay faithful to TutorClass.kind.
 */
export function buildMuscleMemoryQueue(
  tutorClass: TutorClass,
  selectedMinutes: number,
): PracticeSlot[] {
  const seeds = seedPieces(tutorClass);
  if (seeds.length === 0) return [];

  const kind = tutorClass.kind;
  const targetSlots = Math.max(14, Math.round(selectedMinutes * 3));
  const warmFrac = authoredWarmFraction(kind);

  const allVariants: Array<{
    seed: MusicPiece;
    title: string;
    notes: NoteEvent[];
    difficulty: number;
    gen: number;
  }> = [];

  const rounds = Math.max(2, Math.ceil(selectedMinutes / 12));
  for (let r = 0; r < rounds; r++) {
    for (const seed of seeds) {
      for (const v of variantsForKind(kind, seed, tutorClass.id, r * 17 + 3)) {
        allVariants.push({
          seed,
          title: v.title,
          notes: v.notes,
          difficulty: v.difficulty + r,
          gen: r,
        });
      }
    }
  }

  allVariants.sort((a, b) => a.difficulty - b.difficulty || a.gen - b.gen);

  const queue: PracticeSlot[] = [];
  const usedKeys = new Set<string>();
  const warmCap = Math.max(4, Math.ceil(targetSlots * warmFrac));

  // Authored segment warm-up — more loops for kind-faithful classes.
  for (const seg of tutorClass.segments) {
    const piece = getPieceById(seg.pieceId);
    if (!piece) continue;
    const warmLoops = Math.max(
      1,
      Math.round(seg.baseLoops * (warmFrac >= 0.3 ? 0.85 : 0.5)),
    );
    for (let i = 0; i < warmLoops; i++) {
      queue.push({
        piece,
        label: `${seg.label ?? piece.title} · warm-up`,
        bpm: bpmForPhase(piece.bpm, "warm", seg),
        metronomeLocked: Boolean(seg.metronomeLocked),
        phase: "warm",
      });
      if (queue.length >= warmCap) break;
    }
    if (queue.length >= warmCap) break;
  }

  let vi = 0;
  while (queue.length < targetSlots && vi < allVariants.length * 4) {
    const v = allVariants[vi % allVariants.length];
    vi += 1;
    const t = queue.length / targetSlots;
    const phase = phaseForProgress(t);
    const key = `${v.seed.id}:${v.title}:${v.gen}:${phase}:${queue.length}`;
    if (usedKeys.has(key) && vi < allVariants.length * 2) continue;
    usedKeys.add(key);

    const matchingSeg = tutorClass.segments.find((s) => s.pieceId === v.seed.id);
    const bpm = bpmForPhase(v.seed.bpm, phase, matchingSeg);
    const locked =
      phase === "lock" ||
      phase === "seal" ||
      Boolean(matchingSeg?.metronomeLocked);
    const piece = makePiece(
      `mm-${tutorClass.id}-${queue.length}-${v.gen}`,
      `${v.title}`,
      v.seed,
      v.notes,
      bpm,
    );

    const phaseLabel =
      phase === "warm"
        ? "Warm"
        : phase === "build"
          ? "Build"
          : phase === "vary"
            ? "Challenge"
            : phase === "lock"
              ? "Locked"
              : "Seal";

    queue.push({
      piece,
      label: `${phaseLabel} · ${v.title}`,
      bpm,
      metronomeLocked: locked,
      phase,
    });
  }

  return queue;
}

/** Test helper: share of slots whose labels look like generic pitch walks. */
export function countPitchWalkLabels(queue: PracticeSlot[]): number {
  return queue.filter((s) =>
    /Walk up|Walk down|Up & down|Snake path|Shuffled order|Neighbor zig|Combo [A-Z]/i.test(
      s.label,
    ),
  ).length;
}
